import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { confirmDiscardAsync } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { LL } from 'i18n/i18n.service';
import { UserDb } from 'services/user-db.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { LoggingService } from 'services/logging.service';
import { ScaledImage } from '@cf-shared/data-model/scaled-image';
import { CoachBranding } from '@cf-shared/data-model/coach';
import {
  deriveBrandPalette,
  parseHex,
  rgbToHex,
  onAccentContrastRatio,
  BrandPalette,
  WCAG_AA_CONTRAST,
} from '@cf-shared/color-math';
import { CoachBrandingPageParams } from './coach-branding-interface';
import { CoachBrandingStateService } from 'services/coach-branding-state.service';
import { TranslatedString } from 'models/translated-string';

// A few curated, accessible accent presets the coach can start from. Each is a
// strong, mid-to-dark hue that yields good on-accent contrast on the dark UI.
// The coach can still type any free-form hex; these are just starting points.
const ACCENT_PRESETS: string[] = [
  '#116DD5', // MH blue (the default)
  '#208056', // green
  '#7559B3', // purple
  '#AD2B89', // magenta
  '#1B7F7B', // teal
  '#A94B0A', // orange
];

// Max characters for the brand name input.
const BRAND_NAME_MAX_LENGTH = 40;

// How long the result toasts stay on screen (ms). A toast with no duration and
// no close button never auto-dismisses (see ToastOptions.duration), so every
// toast here MUST pass one. Success is short; the warning/error variants linger
// longer so the coach has time to read the failure.
const SUCCESS_TOAST_DURATION_MS = 3000;
const PROBLEM_TOAST_DURATION_MS = 5000;

// Minimum accepted logo size (shorter edge, px). A logo renders small in the
// app (up to ~64px tall on the awaiting screen, 28px in the menu), so the
// default photo minimum (1080px) is far too strict — this low floor accepts
// real logos while still rejecting tiny / broken images.
const LOGO_MIN_IMAGE_SIZE = 64;

/**
 * Coach Branding setup page (Tier 1 white-labeling, #3707).
 *
 * The coach sets three things — logo, brand name, one accent color — and the
 * single accent hex drives a live preview (sample button + text) using the same
 * deriveBrandPalette() math the client app themes with. An inline contrast
 * warning appears when the best achievable on-accent text contrast falls below
 * WCAG AA, so the coach is nudged away from an unreadable accent (the app still
 * renders it legibly via the contrast-safe on-accent color).
 *
 * On save we call coachUpdateBranding, which writes the coach's source-of-truth
 * as-coach branding AND fans the new branding out to every already-claimed
 * client. New clients inherit the latest branding at claim time separately.
 */
@Component({
  selector: 'page-coach-branding',
  templateUrl: 'coach-branding.html',
  host: { 'class': 'coach-branding-page' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoachBrandingPage extends CsPage<CoachBrandingPageParams> {
  protected LL = LL;
  protected readonly presets = ACCENT_PRESETS;
  protected readonly logoMinImageSize = LOGO_MIN_IMAGE_SIZE;

  // editable fields
  brandName = '';
  accentColorHex = '';
  // the logo as the image-uploader understands it; null when no logo set.
  logoImage: ScaledImage | null = null;
  private logoUrl: string | undefined;
  private logoPath: string | undefined;

  isSaving = false;
  /** What was stored when the page opened, so the guard can tell an edit from a load. */
  private brandingAsLoaded = { brandName: '', accentColorHex: '', logoUrl: undefined as string | undefined };

  constructor(
    public navCtrl: NavController,
    private userDb: UserDb,
    private cloudFunctions: CloudFunctionsService,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private logging: LoggingService,
    private coachBrandingState: CoachBrandingStateService,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewWillEnter() {
    const branding = this.userDb.loadCoachRelAsCoach()?.branding;
    this.brandName = branding?.brandName ?? '';
    this.accentColorHex = branding?.accentColorHex ?? '';
    this.logoUrl = branding?.logoUrl;
    this.logoPath = branding?.logoPath;
    // reconstruct a one-size ScaledImage so the uploader can show the existing
    // logo. width/height are unused by the srcset render path.
    this.logoImage = this.logoUrl
      ? { sizes: [{ url: this.logoUrl, path: this.logoPath ?? '', width: 1, height: 1 }] }
      : null;
    // Snapshot AFTER every field is filled in, so a page that merely opened does
    // not look edited. ionViewWillEnter runs again on every return to the page,
    // which is what we want: coming back from a sub-page re-reads the stored
    // brand, so the baseline should follow it.
    this.brandingAsLoaded = {
      brandName: this.brandName,
      accentColorHex: this.accentColorHex,
      logoUrl: this.logoUrl,
    };
    this.cd.markForCheck();
  }

  /**
   * Runs before the page closes, whichever way the coach leaves: the back arrow,
   * the browser back button, Android's hardware back button or a swipe back.
   * Without it, a logo they had just uploaded and a colour they had just picked
   * were thrown away in silence (#5058).
   *
   * Saving here fans the brand out to every claimed client, which is why it is a
   * deliberate button press and not something to offer on the way out — so the
   * question is only whether they meant to drop the changes.
   */
  async ionViewCanLeave(): Promise<boolean> {
    // the save path has already written the brand, so it arms this and there is
    // nothing left to ask about
    if (this.leaveWithoutAsking.consume()) return true;
    if (!this.hasUnsavedBrandingEdits()) return true;
    return confirmDiscardAsync(this.alertCtrl, {
      title: LL.unsavedChanges.discard.brandingTitle(),
      message: LL.unsavedChanges.discard.brandingMessage(),
    });
  }

  /** Whether anything on screen differs from the brand that was stored. */
  private hasUnsavedBrandingEdits(): boolean {
    return this.brandName !== this.brandingAsLoaded.brandName
      || this.accentColorHex !== this.brandingAsLoaded.accentColorHex
      || this.logoUrl !== this.brandingAsLoaded.logoUrl;
  }

  // --- accent color ---

  selectPreset(hex: string) {
    this.accentColorHex = hex;
    this.cd.markForCheck();
  }

  onAccentInput(value: string) {
    this.accentColorHex = value;
    this.cd.markForCheck();
  }

  /** The derived preview palette, or null when the accent is invalid/empty. */
  get previewPalette(): BrandPalette | null {
    return deriveBrandPalette(this.accentColorHex);
  }

  /** True when the current accent string parses to a valid hex color. */
  get isAccentValid(): boolean {
    // a non-null palette means the accent parsed — reuse it rather than
    // re-parsing the hex separately.
    return this.previewPalette !== null;
  }

  /**
   * True when a valid accent is set but its best on-accent text contrast is
   * below WCAG AA — surfaces the inline warning. (The app still renders the
   * accent legibly; this only nudges the coach toward a better choice.)
   */
  get hasLowContrastWarning(): boolean {
    if (!this.isAccentValid) return false;
    return onAccentContrastRatio(this.accentColorHex) < WCAG_AA_CONTRAST;
  }

  // --- logo ---

  onLogoUpdated(image: ScaledImage | null) {
    this.logoImage = image;
    const size = image?.sizes?.[0];
    this.logoUrl = size?.url;
    this.logoPath = size?.path;
    this.cd.markForCheck();
  }

  // --- save ---

  /** True when there is enough to save (a valid accent is the minimum). */
  get canSave(): boolean {
    return this.isAccentValid && !this.isSaving;
  }

  /**
   * True when the coach currently has a saved brand (a stored accent on their
   * as-coach doc). Drives whether the "Reset to Default" action is shown — there
   * is nothing to reset when no brand was ever saved.
   */
  get hasExistingBranding(): boolean {
    return !!this.userDb.loadCoachRelAsCoach()?.branding?.accentColorHex;
  }

  async saveAsync() {
    if (!this.canSave) return;

    // confirm first: saving fans the brand out to every already-claimed client.
    // make that consequence explicit before we write anything.
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.coach.branding.saveConfirmTitle(),
      message: LL.coach.branding.saveConfirmMessage(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.coach.branding.save(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    if (!confirmed) return;

    // cs-input has no maxlength, so cap the brand name here.
    const trimmedName = this.brandName.trim().slice(0, BRAND_NAME_MAX_LENGTH);
    // normalize the accent to canonical #RRGGBB before persisting. canSave
    // already guarantees parseHex succeeds, but the coach may have typed
    // shorthand (#fff), no leading #, lowercase, or whitespace — store the
    // canonical form so the data-model contract holds and the early inline
    // splash script (which uses the raw cached value as a CSS color) gets a
    // valid value. The `!` is safe: canSave gated on isAccentValid.
    const normalizedAccent = rgbToHex(parseHex(this.accentColorHex)!);
    const branding: CoachBranding = {
      logoUrl: this.logoUrl,
      logoPath: this.logoPath,
      brandName: trimmedName || undefined,
      accentColorHex: normalizedAccent,
      // server stamps the authoritative updatedAt; omit here.
    };

    await this.submitBrandingAsync(branding, {
      success: LL.coach.branding.saveSuccess(),
      partial: (count) => LL.coach.branding.savePartial({ count }),
      error: LL.coach.branding.saveError(),
    });
  }

  /**
   * Reset the coach's branding back to the default MH look, for themselves and
   * for every already-claimed client.
   *
   * We do this by saving an EMPTY branding object. The server stamps it with a
   * fresh `updatedAt` and fans it out exactly like a normal edit — but because it
   * carries no accent (and no logo or name), the client app derives a null
   * palette and falls back to MH everywhere. The empty object is effectively a
   * "tombstone": it keeps the fan-out and `updatedAt` idempotency working with no
   * special server path. We deliberately leave the uploaded logo file in Google
   * Cloud Storage — clearing the reference is enough to revert the look, and
   * storage cleanup is out of scope here.
   */
  async resetAsync() {
    if (this.isSaving) return;

    // confirm first: like save, this fans out to every claimed client.
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.coach.branding.resetConfirmTitle(),
      message: LL.coach.branding.resetConfirmMessage(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.common.buttons.remove(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    if (!confirmed) return;

    // an empty branding is the reset tombstone — server stamps updatedAt.
    const empty: CoachBranding = {};
    await this.submitBrandingAsync(empty, {
      success: LL.coach.branding.resetSuccess(),
      partial: (count) => LL.coach.branding.resetPartial({ count }),
      error: LL.coach.branding.resetError(),
    });
  }

  /**
   * Shared submit path for both Save and Reset. Writes the branding via
   * coachUpdateBranding (which fans it out to every claimed client), themes the
   * coach's own app immediately so they see the change without a reload, shows
   * the right result toast, and pops on success. Save and Reset differ only in
   * the payload and the three result messages, so the caller hands those in —
   * keeping the two paths from drifting. `messages.partial` takes the failed
   * client count; `success` / `error` are already-resolved strings.
   */
  private async submitBrandingAsync(
    branding: CoachBranding,
    messages: {
      success: TranslatedString;
      partial: (count: number) => TranslatedString;
      error: TranslatedString;
    },
  ) {
    this.isSaving = true;
    this.cd.markForCheck();

    try {
      const result = await this.cloudFunctions.coachUpdateBrandingAsync({ branding });
      // theme the coach's OWN app immediately — the server write that persists
      // the as-coach doc has not synced back to local PouchDB yet, so we hand the
      // in-memory branding straight to the app rather than waiting for a reload.
      // an empty branding clears the overrides and falls back to MH.
      this.coachBrandingState.applyBrandingValue(branding);
      if (result.failedClientCount > 0) {
        // partial success: the change saved, but some clients did not re-theme.
        this.toastCtrl.showToast({
          message: messages.partial(result.failedClientCount),
          type: ToastType.WarningLight,
          duration: PROBLEM_TOAST_DURATION_MS,
        });
      } else {
        this.toastCtrl.showToast({
          message: messages.success,
          type: ToastType.SuccessStrong,
          duration: SUCCESS_TOAST_DURATION_MS,
        });
      }
      // the brand has just been written, so there is nothing left to ask about
      this.leaveWithoutAsking.set();
      await this.navCtrl.popAsync();
    } catch (err: any) {
      // surface the failure: the coach must know the change did not save.
      this.logging.trackAbnormalException(err);
      this.toastCtrl.showToast({
        message: messages.error,
        type: ToastType.WarningStrong,
        duration: PROBLEM_TOAST_DURATION_MS,
      });
    } finally {
      this.isSaving = false;
      this.cd.markForCheck();
    }
  }
}
